import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Modal from '../../../../components/Modal'
import Loader from '../../../../components/Loader'
import Toast from '../../../../components/Toast/Toast'
import { createContractDocument } from '../../../../services/NewServicesCompnay'
import { TAGS } from '../../../../services/Tags'
import { globalBootstrap } from '../../../..'

const MODAL_ID = 'create-contract-document-modal'

const PROFILE_ID = {
  TALENT: 1,
  COMPANY: 3,
}

const PROFILE_OPTIONS = [
  { value: PROFILE_ID.TALENT, label: 'Talento' },
  { value: PROFILE_ID.COMPANY, label: 'Empresa' },
]

const WORD_MIME_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

const isWordFile = (selectedFile) => {
  if (!selectedFile) return false
  const extension = selectedFile.name?.split('.').pop()?.toLowerCase()
  return (
    WORD_MIME_TYPES.includes(selectedFile.type) ||
    extension === 'doc' ||
    extension === 'docx'
  )
}

const CreateContractDocumentModal = () => {
  const queryClient = useQueryClient()
  const fileInputRef = useRef(null)

  const [file, setFile] = useState(null)
  const [name, setName] = useState('')
  const [taxes, setTaxes] = useState('')
  const [amount, setAmount] = useState('')
  const [months, setMonths] = useState('')
  const [taxApplies, setTaxApplies] = useState(true)
  const [indefiniteTime, setIndefiniteTime] = useState(false)
  const [profileId, setProfileId] = useState(PROFILE_ID.COMPANY)
  const [fileError, setFileError] = useState('')

  const resetForm = () => {
    setFile(null)
    setName('')
    setTaxes('')
    setAmount('')
    setMonths('')
    setTaxApplies(true)
    setIndefiniteTime(false)
    setProfileId(PROFILE_ID.COMPANY)
    setFileError('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const { mutate: createDocument, isLoading } = useMutation(createContractDocument, {
    onSuccess: ({ response, res }) => {
      if (!response?.ok) {
        Toast({
          icon: 'error',
          title: res?.msg || res?.error || 'No se pudo crear el contrato',
        })
        return
      }

      queryClient.invalidateQueries([TAGS.contract_documents])
      queryClient.invalidateQueries(['contract-documents'])
      Toast({ title: 'Contrato creado correctamente' })
      resetForm()
      globalBootstrap.Modal.getOrCreateInstance(`#${MODAL_ID}`).hide()
    },
    onError: () => {
      Toast({ icon: 'error', title: 'No se pudo crear el contrato' })
    },
  })

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return
    if (!isWordFile(selectedFile)) {
      setFileError('Solo se permiten archivos Word (.doc, .docx).')
      setFile(null)
      return
    }
    setFileError('')
    setFile(selectedFile)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!file) {
      setFileError('Debes seleccionar un archivo Word.')
      return
    }

    if (!name.trim() || !taxes.trim() || !amount.trim()) {
      Toast({ icon: 'error', title: 'Completa los campos obligatorios' })
      return
    }

    if (!indefiniteTime && !months.trim()) {
      Toast({ icon: 'error', title: 'Indica la duración en meses o marca tiempo indefinido' })
      return
    }

    createDocument({
      file,
      name: name.trim(),
      taxes: taxes.trim(),
      amount: amount.trim(),
      taxApplies,
      indefiniteTime,
      months: indefiniteTime ? '0' : months.trim(),
      profileId,
    })
  }

  return (
    <Modal
      modalId={MODAL_ID}
      title="Nuevo contrato"
      titleClassName="fw-bolder text-on-bg-primary"
      modalSize="modal-lg"
    >
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label text-on-bg-primary fw-bold" style={{ fontSize: '13px' }}>
            Nombre *
          </label>
          <input
            type="text"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre del contrato"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label text-on-bg-primary fw-bold d-block mb-2" style={{ fontSize: '13px' }}>
            Tipo de perfil *
          </label>
          <div className="d-flex flex-wrap gap-3">
            {PROFILE_OPTIONS.map((option) => (
              <div className="form-check" key={option.value}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="profileId"
                  id={`profile-${option.value}`}
                  value={option.value}
                  checked={profileId === option.value}
                  onChange={() => setProfileId(option.value)}
                />
                <label className="form-check-label" htmlFor={`profile-${option.value}`}>
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label text-on-bg-primary fw-bold" style={{ fontSize: '13px' }}>
            Archivo Word *
          </label>
          <input
            ref={fileInputRef}
            type="file"
            className="form-control"
            accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => handleFileChange(e.target.files?.[0])}
          />
          {file && (
            <p className="mb-0 mt-1" style={{ fontSize: '12px', color: '#166534' }}>
              Archivo seleccionado: {file.name}
            </p>
          )}
          {fileError && (
            <p className="mb-0 mt-1" style={{ fontSize: '12px', color: '#991b1b' }}>
              {fileError}
            </p>
          )}
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label text-on-bg-primary fw-bold" style={{ fontSize: '13px' }}>
              Impuestos (%) *
            </label>
            <input
              type="text"
              className="form-control"
              value={taxes}
              onChange={(e) => setTaxes(e.target.value)}
              placeholder="15"
              required
            />
          </div>
          <div className="col-md-6">
            <label className="form-label text-on-bg-primary fw-bold" style={{ fontSize: '13px' }}>
              Monto *
            </label>
            <input
              type="text"
              className="form-control"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="500"
              required
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label text-on-bg-primary fw-bold" style={{ fontSize: '13px' }}>
            Meses
          </label>
          <input
            type="text"
            className="form-control"
            value={months}
            onChange={(e) => setMonths(e.target.value)}
            placeholder="4"
            disabled={indefiniteTime}
          />
        </div>

        <div className="d-flex flex-column gap-2 mb-4">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="create-tax-applies"
              checked={taxApplies}
              onChange={(e) => setTaxApplies(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="create-tax-applies">
              Aplica impuestos
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="create-indefinite-time"
              checked={indefiniteTime}
              onChange={(e) => setIndefiniteTime(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="create-indefinite-time">
              Tiempo indefinido
            </label>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-white border"
            data-bs-dismiss="modal"
            onClick={resetForm}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-bg-CTA-primary d-flex align-items-center justify-content-center gap-2"
            disabled={isLoading}
            style={{ minWidth: '140px' }}
          >
            {isLoading ? <Loader /> : 'Crear contrato'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default CreateContractDocumentModal
